<template>
    <LoadingState :loading="loading" />
    <div
        v-if="!loading && loungeError"
        class="px-8 py-24 bg-slate-100 dark:bg-slate-700 flex justify-center"
    >
        <EmptyState
            title="Lounge Not Found"
            :description="loungeError"
            :hasAction="true"
            actionLabel="Back to Lounge Finder"
            @action="router.visit('/lounges')"
        >
            <template #image>
                <div
                    class="w-20 h-20 bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center mb-5"
                >
                    <svg
                        width="25"
                        height="25"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8 6.01278V8.00895M8 10H8.00667M6.85097 2.66024L2.18321 10.6654C1.6659 11.5525 2.30558 12.6667 3.33224 12.6667H12.6678C13.6944 12.6667 14.3341 11.5525 13.8168 10.6654L9.14903 2.66024C8.63572 1.77992 7.36428 1.77992 6.85097 2.66024ZM8.16667 10C8.16667 10.092 8.09205 10.1667 8 10.1667C7.90795 10.1667 7.83333 10.092 7.83333 10C7.83333 9.90795 7.90795 9.83333 8 9.83333C8.09205 9.83333 8.16667 9.90795 8.16667 10Z"
                            stroke="currentColor"
                            stroke-width="1.33333"
                            stroke-linecap="round"
                            class="text-slate-800 dark:text-slate-100"
                        />
                    </svg>
                </div>
            </template>
        </EmptyState>
    </div>
    <div
        v-if="!loading && activeLounge"
        class="px-8 pb-24 bg-slate-100 dark:bg-slate-900 flex-col justify-start items-start flex"
    >
        <div id="top"></div>
        <!-- START LOUNGE BANNER -->
        <div class="relative pb-16 w-full" id="banner">
            <section class="grid grid-cols-12 gap-4 w-full">
                <div
                    class="col-span-12 lg:col-span-8 mx-auto max-w-full w-full overflow-hidden relative h-96 rounded-lg"
                >
                    <ImageLoader>
                        <template #image>
                            <img
                                v-if="activeLounge?.featured_image.src"
                                :src="activeLounge?.featured_image.src"
                                :alt="activeLounge?.name"
                                class="w-full h-96 object-cover object-center rounded-lg"
                            />
                        </template>
                        <template #error>
                            <div
                                class="w-full h-96 rounded-lg object-cover flex items-center justify-center text-center flex-col bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 dark:text-slate-400"
                            >
                                <Icon icon="no-image" />
                            </div>
                        </template>
                    </ImageLoader>
                </div>

                <div
                    class="hidden lg:block col-span-12 min-h-44 md:col-span-4 bg-gray-200 dark:bg-slate-700"
                >
                    <SmallMap />
                </div>
            </section>
            <!-- LG MAP PLACEHOLDER -->
            <div class="fixed inset-0 z-[999]" v-if="showMap">
                <Teleport to="body">
                    <Suspense>
                        <Map
                            :loading="loading"
                            :showMap="showMap"
                            :mapMarkers="mapMarkers"
                            :mapType="mapType"
                            :mapCenter="mapCenter"
                            @closeMap="() => setShowMap(false)"
                        >
                            <template
                                #popup="{
                                    closeHandler,
                                    infoWindowContent,
                                    show,
                                }"
                            >
                                <LoungeInfoWindow
                                    :infoWindowContent="infoWindowContent"
                                    :show="show"
                                    @favoriteChanged="(v) => (isFavorite = v)"
                                    @close="closeHandler"
                                />
                            </template>
                        </Map>
                    </Suspense>
                </Teleport>
            </div>

            <!--  Property Details Section -->
            <div class="grid grid-cols-12 gap-4 items-center">
                <div class="col-span-4 flex justify-end"></div>
            </div>
            <div
                class="pt-8 bg-slate-100 dark:bg-slate-900 justify-between flex flex-col md:flex-row"
            >
                <div class="w-full flex-col justify-start items-start mb-5">
                    <div>
                        <div class="flex justify-between">
                            <div>
                                <h1
                                    class="text-slate-800 dark:text-slate-100 text-xl md:text-3xl font-medium pr-2 mb-4"
                                >
                                    {{ activeLounge.name.short }}
                                </h1>
                                <!-- START LOCATION -->
                                <div class="flex items-center gap-2">
                                    <p class="text-slate-500 dark:text-slate-400 text-sm">
                                        {{ activeLounge.airport?.name }}
                                    </p>
                                    <div class="w-1 h-1 relative">
                                        <div
                                            class="w-0.5 h-0.5 left-[0.50px] top-[0.25px] absolute bg-slate-500 rounded-full"
                                        ></div>
                                    </div>
                                    <p class="text-slate-500 dark:text-slate-400 text-sm">
                                        {{ activeLounge.location }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-center gap-3">
                                <span
                                    class="w-10 h-10 rounded shadow border border-slate-200 dark:border-slate-700"
                                >
                                    <FavButton
                                        buttonClass="bg-white dark:bg-slate-800 p-2.5 rounded"
                                        :isFavorite="
                                            isFavorite ? isFavorite : false
                                        "
                                        @toggleFavorite="handleToggleFavorite"
                                        id="hs-lounge-detail-fav-btn"
                                    />
                                </span>
                            </div>
                        </div>
                        <!-- END LOCATION -->
                        <div
                            class="pt-4 justify-start items-start flex flex-col"
                        >
                            <div
                                class="flex justify-start gap-6 sm:flex-row flex-col"
                            >
                                <LoungeHours
                                    :isOpen="activeLounge.open_now"
                                    :hoursData="activeLounge.business_hours"
                                    :timing="activeLounge.timing"
                                    v-if="
                                        activeLounge.business_hours ||
                                        activeLounge.timing
                                    "
                                />
                                <div
                                    v-if="activeLounge.website"
                                    class="rounded justify-start items-center gap-2 flex text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight"
                                >
                                    <Icon
                                        icon="globe"
                                        svgClass="w-5 h-5 fill-none"
                                    />
                                    <a
                                        :href="activeLounge.website"
                                        target="_blank"
                                        class="btn-link focus:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 focus:outline-slate-300 rounded"
                                        id="hs-lounge-website-link"
                                    >
                                        Lounge website
                                    </a>
                                </div>
                            </div>
                            <!-- START OPERATOR AND ALLIANCE -->
                            <div
                                class="pt-8 justify-start items-start gap-12 flex"
                            >
                                <div
                                    class="flex-col justify-center items-start gap-1 flex"
                                >
                                    <h3
                                        class="self-stretch text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 text-sm font-normal leading-tight"
                                    >
                                        Operated by
                                    </h3>
                                    <div
                                        class="justify-start items-center gap-2 flex"
                                    >
                                        <img
                                            v-if="activeLounge.operator?.image"
                                            class="bg-white dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700 justify-center items-center gap-2.5 flex w-24 h-9 lg:w-36 lg:h-12 lg:px-2"
                                            :src="`${cdnUrl}/${activeLounge.operator?.image?.path}/${activeLounge.operator?.image?.file_name}`"
                                        />
                                        <div
                                            v-else
                                            class="items-center gap-2.5 flex w-24 h-9 lg:w-36 lg:h-12"
                                        >
                                            <div
                                                class="w-full h-full flex items-center justify-start"
                                            >
                                                <LoungeIcon
                                                    icon="airplane"
                                                    class="shrink-0 bg-slate-200 dark:bg-slate-700 p-1 rounded-lg"
                                                />
                                                <span
                                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight ml-2"
                                                >
                                                    {{
                                                        activeLounge?.operator
                                                            ?.name
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="
                                        activeLounge.alliance &&
                                        activeLounge.alliance !== 'None'
                                    "
                                    class="flex-col justify-center items-start gap-1 flex"
                                >
                                    <h3
                                        class="self-stretch text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 text-sm font-normal leading-tight"
                                    >
                                        Alliance
                                    </h3>
                                    <div
                                        class="bg-white dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700 justify-center items-center gap-2.5 flex w-24 h-9 lg:w-36 lg:h-12 lg:px-2"
                                    >
                                        <div
                                            v-if="
                                                activeLounge.alliance &&
                                                activeLounge.alliance !== 'None'
                                            "
                                            class="items-center justify-center flex w-24 h-9 lg:w-36 lg:h-12"
                                        >
                                            <LoungeIcon
                                                v-if="
                                                    activeLounge.alliance ==
                                                    'OneWorld'
                                                "
                                                icon="one_world_alliance_logo"
                                                class="shrink-0 p-1 rounded-lg lg:h-10 lg:w-10 w-8 h-8"
                                            />
                                            <LoungeIcon
                                                v-else-if="
                                                    activeLounge.alliance ==
                                                    'Star Alliance'
                                                "
                                                icon="star_alliance_logo"
                                                class="shrink-0 p-1 rounded-lg lg:h-10 lg:w-10 w-8 h-8"
                                            />
                                            <LoungeIcon
                                                v-else-if="
                                                    activeLounge.alliance ==
                                                    'SkyTeam'
                                                "
                                                icon="sky_team_alliance_logo"
                                                class="shrink-0 p-1 rounded-lg lg:h-10 lg:w-10 w-8 h-8"
                                            />
                                        </div>
                                        <div
                                            v-else
                                            class="items-center gap-2.5 flex w-24 h-9 lg:w-36 lg:h-12"
                                        >
                                            <div
                                                class="w-full h-full flex items-center justify-start"
                                            >
                                                <Icon
                                                    icon="globe"
                                                    class="shrink-0 bg-slate-200 dark:bg-slate-700 p-1 rounded-lg"
                                                />
                                                <span
                                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight ml-2"
                                                >
                                                    {{ activeLounge.alliance }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- END OPERATOR AND ALLIANCE -->
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- END BANNER -->
        <!-- START OVERVIEW -->
        <div
            class="self-stretch flex-col justify-start items-start flex pb-16"
            v-if="computedOverview"
        >
            <h2
                class="self-stretch pb-6 text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
            >
                Overview
            </h2>
            <div
                class="self-stretch flex-col justify-start items-start flex bg-white dark:bg-slate-800 p-6 rounded-lg shadow dark:shadow-slate-900/50"
            >
                <ExpandableSection
                    sectionClass="flex-col justify-start items-start gap-4 flex transition-all ease-in-out duration-300 relative prose"
                >
                    <template #content>
                        <div
                            class="lounge-overview-text text-slate-800 dark:text-slate-100 text-sm font-normal leading-tight"
                            v-safe-html="computedOverview"
                        ></div>
                    </template>
                </ExpandableSection>
            </div>
        </div>
        <!-- END OVERVIEW -->
        <!-- START AMENITIES -->
        <div
            class="self-stretch flex-col justify-start items-start flex pb-16"
            v-if="sortedAmenities.length"
        >
            <h2
                class="self-stretch pb-6 text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
            >
                Amenities
            </h2>
            <div
                class="self-stretch flex-col justify-start items-start flex bg-white dark:bg-slate-800 p-6 rounded-lg shadow dark:shadow-slate-900/50"
            >
                <ExpandableSection
                    sectionClass="self-stretch flex-col justify-start items-start gap-4 flex transition-all ease-in-out duration-300 relative"
                >
                    <template #content>
                        <div
                            class="gap-6 grid sm:grid-cols-2 grid-flow-row w-full"
                        >
                            <AmenityListItem
                                v-for="amenity in sortedAmenities"
                                :key="amenity.value"
                                :label="amenity.label"
                                :icon="amenity.icon"
                            />
                        </div>
                    </template>
                </ExpandableSection>
            </div>
        </div>
        <!-- START ACCESS -->
        <div
            class="self-stretch flex-col justify-start items-start flex pb-16"
            v-if="computedAccess"
        >
            <h2
                class="self-stretch pb-6 text-slate-800 dark:text-slate-100 text-lg font-medium leading-7"
            >
                Access
            </h2>
            <div
                class="self-stretch flex-col justify-start items-start flex bg-white dark:bg-slate-800 p-6 rounded-lg shadow dark:shadow-slate-900/50"
            >
                <ExpandableSection
                    sectionClass="self-stretch flex-col justify-start items-start gap-4 flex transition-all ease-in-out duration-300 relative"
                >
                    <template #content>
                        <div
                            class="lounge-access-text text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                            v-safe-html="computedAccess"
                        ></div>
                    </template>
                </ExpandableSection>
            </div>
        </div>
        <!-- START AIRLINES -->
        <div
            class="self-stretch flex-col justify-start items-start flex pb-16"
            v-if="activeLounge?.airline.length"
        >
            <h2
                class="pb-6 flex-col justify-center items-start gap-1 flex self-stretch text-slate-800 dark:text-slate-100 text-lg font-medium font-['Inter'] leading-7"
            >
                Airlines
            </h2>
            <div
                class="self-stretch p-6 bg-white dark:bg-slate-800 rounded-lg shadow flex-col justify-start items-start flex"
            >
                <div
                    class="self-stretch grow shrink basis-0 flex-col justify-start items-start flex"
                >
                    <p
                        class="self-stretch text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                    >
                        You may have access to the
                        {{ activeLounge.name.short }} as a premium customer of
                        one of the following airlines, or as an elite member of
                        their frequent flyer program. Check the Access rules
                        section for more details.
                    </p>
                    <div
                        class="self-stretch pt-8 flex-col justify-start items-start flex"
                    >
                        <div
                            v-if="
                                activeLounge.alliance &&
                                activeLounge.alliance !== 'None'
                            "
                            class="w-full flex-col justify-start items-start gap-1 flex mb-6"
                        >
                            <h3
                                class="self-stretch text-slate-800 dark:text-slate-100 text-base font-medium font-['Inter'] leading-normal"
                            >
                                {{ activeLounge.alliance }}
                            </h3>
                            <p
                                class="self-stretch text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 text-sm font-normal font-['Inter'] leading-tight"
                            >
                                Access for eligible customers traveling on any
                                {{ activeLounge.alliance }} member airline.
                            </p>
                        </div>
                        <ExpandableSection
                            sectionClass="w-full flex-col justify-start items-start flex transition-all ease-in-out duration-300 relative"
                        >
                            <template #content>
                                <div
                                    class="py-3 gap-4 grid grid-cols-2 lg:grid-cols-3 w-full"
                                >
                                    <OperatorItem
                                        v-for="airline in activeLounge?.airline"
                                        :name="airline.name"
                                        :code="airline.code"
                                        :img="airline.image"
                                    />
                                </div>
                            </template>
                        </ExpandableSection>
                    </div>
                </div>
            </div>
        </div>
        <!-- START NEARBY LOUNGES -->
        <div
            class="self-stretch flex-col justify-start items-start flex"
            v-if="nearbyLounges.length"
        >
            <h2
                class="flex text-slate-800 dark:text-slate-100 text-lg font-medium leading-7 self-stretch justify-start items-start"
            >
                Other lounges nearby
            </h2>
            <div
                class="self-stretch pt-4 justify-start items-start gap-6 flex flex-wrap w-full"
            >
                <LoungeCard v-for="lounge in nearbyLounges" :lounge="lounge" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3';
import { onMounted, computed, ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import { useLoungesStore } from '@/store/lounges';
import AmenityListItem from '@/components/tools/lounges/partials/amenity-list-item/amenity-list-item.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import ExpandableSection from '@/components/ui/expandable-section/ui-expandable-section.vue';
import FavButton from '@/components/ui/fav-button/fav-button.vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import LoungeCard from '@/components/tools/lounges/partials/lounge-card/lounge-card.vue';
import LoungeHours from '@/components/tools/lounges/partials/lounge-hours/lounge-hours.vue';
import LoungeIcon from '@/components/tools/lounges/partials/lounge-icon/lounge-icon.vue';
import LoungeInfoWindow from '@/components/tools/lounges/partials/lounge-infowindow/lounge-infowindow.vue';
import Map from '@/components/tools/lounges/partials/lounge-map/lounge-map.vue';
import SmallMap from '@/components/tools/lounges/lounge/lounge-small-map/lounge-small-map.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import OperatorItem from '@/components/tools/lounges/partials/operator-item/operator-item.vue';
import _ from 'lodash';

const appStore = useAppStore();
const loungesStore = useLoungesStore();
const { setBreadcrumbs } = appStore;
const {
    activeLounge,
    filteredSearchResults,
    loading,
    loungeError,
    mapCenter,
    mapMarkers,
    mapType,
    searchResults,
    showMap,
} = storeToRefs(loungesStore);
const {
    getLounge,
    searchLounges,
    setActiveAirport,
    setMapType,
    setShowMap,
    toggleFavorite,
} = loungesStore;

const showAffiliateModal = () => {
    appStore.setShowAffiliateModal(true);
};

const isFavorite = ref(activeLounge.value?.favorite ?? false);

const cdnUrl = computed(() => {
    return window.cdnUrl;
});
const handleToggleFavorite = async () => {
    const previousState = isFavorite.value;
    isFavorite.value = !previousState;

    try {
        const res = await toggleFavorite(activeLounge.value, 'lounge');
        if (!res || res.status !== 200) {
            isFavorite.value = previousState;
        }
    } catch (error) {
        isFavorite.value = previousState;
    }
};

const decodeHtml = (html) => {
    if (!html) return null;
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
};

const computedOverview = computed(() =>
    decodeHtml(activeLounge.value?.content)
);

const computedAccess = computed(() =>
    decodeHtml(activeLounge.value?.accesspolicy)
);

const sortedAmenities = computed(
    () =>
        activeLounge.value?.amenities?.sort((a, b) =>
            (a.label ?? '').localeCompare(b.label ?? '')
        ) || null
);

const nearbyLounges = computed(() => {
    const terminal = activeLounge.value?.terminal;
    const loungesInTerminal = searchResults.value?.[terminal] || [];
    return loungesInTerminal.filter(
        (lounge) => lounge.id != activeLounge.value?.id
    );
});

const scrollToTop = () => {
    const top = document.getElementById('f-body');
    if (top) {
        top.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }
};

onMounted(() => {
    setMapType('lounge');
});

watch(activeLounge, async (newVal) => {
    if (newVal) {
        if (!searchResults.value) {
            setActiveAirport(newVal?.airport);
            searchLounges();
        }
        //updateBreadcrumb(newVal.name?.short);
        isFavorite.value = newVal.favorite;

        await nextTick();
        scrollToTop();
    } else {
        //updateBreadcrumb(null);
    }
});

onMounted(async () => {
    await getLounge(usePage().props.id);
});
</script>
